import { useEffect, useMemo, useState } from 'react'
import { Input, Typography, Button, Empty, Spin, theme } from 'antd'
import { SearchOutlined, CloseOutlined, UpOutlined, DownOutlined } from '@ant-design/icons'
import { observer } from 'mobx-react-lite'
import { formatDayLabel } from '@/Ship/Helpers/fieldViews'
import { useStore } from '../../Stores'
import { SEARCH_MIN_QUERY_LENGTH } from '../../Stores/MessageStore'
import { stripHtml } from '../../Helpers/stripHtml'
import ChatAvatar from '../common/ChatAvatar'

const SEARCH_DEBOUNCE_MS = 300

const { Text } = Typography

/** Highlights matching parts of text with yellow background */
function HighlightedText({ text, query }: { text: string; query: string }) {
  if (!query.trim()) return <>{text}</>

  const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi')
  const parts = text.split(regex)

  return (
    <>
      {parts.map((part, i) =>
        regex.test(part) ? (
          <mark key={i} style={{ background: 'var(--ant-color-warning-border)', color: 'var(--ant-color-text)', padding: 0, borderRadius: 2 }}>
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </>
  )
}

const PANEL_WIDTH = 320

const ChatSearchPanel = observer(() => {
  const { uiStore, messageStore, userStore } = useStore()
  const { token } = theme.useToken()
  const [hoveredId, setHoveredId] = useState<string | null>(null)

  const query = uiStore.messageSearchQuery
  const results = messageStore.searchResults
  const activeIndex = uiStore.activeSearchIndex
  const chatId = uiStore.activeChatId

  // Debounced server search: typing restarts the timer; the store drops
  // stale responses, so out-of-order arrivals cannot show wrong results
  useEffect(() => {
    if (!chatId) return
    const handle = setTimeout(() => {
      void messageStore.searchMessages(chatId, query)
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(handle)
  }, [query, chatId, messageStore])

  // Leaving the panel (or switching chats) must not leak old results in
  useEffect(() => () => messageStore.clearSearchResults(), [messageStore, chatId])

  const goToResult = (index: number) => {
    if (results.length === 0) return
    const clamped = Math.max(0, Math.min(index, results.length - 1))
    uiStore.setActiveSearchIndex(clamped)

    // Delegate scroll to MessageList via scrollToMessageId (handles page expansion)
    const target = results[clamped]
    if (!target) return
    uiStore.setScrollToMessage(target.id)

    if (uiStore.isCompact) {
      uiStore.closeMessageSearch()
    }
  }

  // Group results by date (memoized — avoids regrouping on every render).
  // Плаский індекс чіпляється тут же: indexOf у рендері давав O(n²) на
  // кожен ховер/навігацію
  const groupedResults = useMemo(() => {
    const groups: { label: string; messages: { msg: (typeof results)[number]; index: number }[] }[] = []
    let currentLabel = ''
    results.forEach((msg, index) => {
      const label = formatDayLabel(msg.timestamp)
      if (label !== currentLabel) {
        currentLabel = label
        groups.push({ label, messages: [] })
      }
      groups[groups.length - 1]?.messages.push({ msg, index })
    })
    return groups
  }, [results])

  return (
    <div
      style={{
        width: PANEL_WIDTH,
        minWidth: PANEL_WIDTH,
        borderLeft: `1px solid ${token.colorBorderSecondary}`,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        background: token.colorBgContainer,
      }}
    >
      {/* Search input header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '10px 12px',
          borderBottom: `1px solid ${token.colorBorderSecondary}`,
        }}
      >
        {/* Іконки-дії — завжди Button type="text": hover/focus/active
            приходять з antd безкоштовно, гола іконка їх не має */}
        <Button
          type="text"
          size="small"
          icon={<CloseOutlined style={{ fontSize: 14 }} />}
          onClick={() => uiStore.closeMessageSearch()}
          style={{ color: token.colorTextSecondary }}
        />
        <Input
          placeholder="Пошук повідомлень…"
          prefix={<SearchOutlined style={{ color: token.colorTextQuaternary }} />}
          value={query}
          onChange={(e) => uiStore.setMessageSearchQuery(e.target.value)}
          allowClear
          autoFocus
        />
      </div>

      {/* Navigation bar */}
      {query.trim() && results.length > 0 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '6px 12px',
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
            background: token.colorFillQuaternary,
          }}
        >
          <Text type="secondary" style={{ fontSize: 12 }}>
            {activeIndex + 1} з {results.length}
          </Text>
          <div style={{ display: 'flex', gap: 4 }}>
            <Button
              type="text"
              size="small"
              icon={<UpOutlined />}
              disabled={activeIndex <= 0}
              onClick={() => goToResult(activeIndex - 1)}
            />
            <Button
              type="text"
              size="small"
              icon={<DownOutlined />}
              disabled={activeIndex >= results.length - 1}
              onClick={() => goToResult(activeIndex + 1)}
            />
          </div>
        </div>
      )}

      {/* Results */}
      <div className="chat-scroll" style={{ flex: 1, overflowY: 'auto' }}>
        {query.trim().length < SEARCH_MIN_QUERY_LENGTH ? (
          <div style={{ padding: 24, textAlign: 'center', color: token.colorTextSecondary }}>
            {query.trim()
              ? `Введіть щонайменше ${SEARCH_MIN_QUERY_LENGTH} символи`
              : 'Введіть текст для пошуку'}
          </div>
        ) : messageStore.isSearching && results.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center' }}>
            <Spin />
          </div>
        ) : results.length === 0 ? (
          <Empty
            description="Нічого не знайдено"
            style={{ marginTop: 48 }}
          />
        ) : (
          <>
            {groupedResults.map((group) => (
              <div key={group.label}>
                {/* Date separator */}
                <div
                  style={{
                    padding: '6px 12px',
                    fontSize: 11,
                    fontWeight: 600,
                    color: token.colorTextSecondary,
                    background: token.colorFillQuaternary,
                    textAlign: 'center',
                  }}
                >
                  {group.label}
                </div>

                {/* Messages in this group */}
                {group.messages.map(({ msg, index: resultIndex }) => {
                  const sender = userStore.getById(msg.senderId)
                  const isActive = resultIndex === activeIndex
                  return (
                    <div
                      key={msg.id}
                      style={{
                        display: 'flex',
                        gap: 8,
                        padding: '8px 12px',
                        cursor: 'pointer',
                        transition: 'background 0.15s',
                        background: isActive ? token.colorPrimaryBg : hoveredId === msg.id ? token.colorFillTertiary : undefined,
                      }}
                      onClick={() => goToResult(resultIndex)}
                      onMouseEnter={() => setHoveredId(msg.id)}
                      onMouseLeave={() => setHoveredId(null)}
                    >
                      <ChatAvatar
                        name={sender ? `${sender.firstName} ${sender.lastName}` : ''}
                        src={sender?.avatarUrl}
                        size={32}
                        onTint={isActive}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <Text strong style={{ fontSize: 13 }}>
                          {sender ? `${sender.firstName} ${sender.lastName}` : 'Невідомий'}
                        </Text>
                        <div
                          style={{
                            fontSize: 13,
                            color: token.colorText,
                            lineHeight: '18px',
                            wordBreak: 'break-word',
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          <HighlightedText text={stripHtml(msg.content)} query={query} />
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  )
})

export default ChatSearchPanel
